import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  DatePicker,
  Typography,
  Card,
  Button,
  Table,
  Tag,
  Collapse,
  Row,
  Col,
  Statistic,
  Avatar,
  Dropdown,
  message,
  List,
  Tabs,
  Segmented,
} from "antd";
import {
  DownOutlined,
  UserOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  DownloadOutlined,
  PlusCircleOutlined,
  MinusCircleOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  BarChartOutlined,
  MoneyCollectOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import dayjs from "dayjs";
import "dayjs/locale/tr";
import localizedFormat from "dayjs/plugin/localizedFormat";
import weekday from "dayjs/plugin/weekday";
import reportService from "../../../services/reports/report.service";
import payrollService from "../../../services/payroll/payroll.service";
import { TimesheetUserData, DailyRecord } from "../../../types/report.types";
import { CompanyPayrollItem, CompanyPayrollResponse } from "../../../types/payroll.types";
import LoadingSpinner from "../../shared/uiInterface/base-ui/LoadingSpinner";
import { useTranslation } from "react-i18next";
import { handleApiError } from "../../../utils/errorHandler";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";

import FooterSection from "../../shared/components/footerSection";
import "./timesheetReport.css";
import "./payrollReport.css";
import { getFileUrl } from "../../../utils/file.utils";
import { createTimesheetPayrollPdf, normalizeTurkishChars } from "../../../utils/exportUtils";
import { exportToExcel as exportToExcelUtil } from "../../../utils/exportUtils";
import * as XLSX from "xlsx";


// Day.js ayarları
dayjs.locale("tr");
dayjs.extend(localizedFormat);
dayjs.extend(weekday);

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Panel } = Collapse;

const TimesheetReport: React.FC = () => {
  // State tanımlamaları
  const [timesheetData, setTimesheetData] = useState<TimesheetUserData[]>([]);
  const [payrollData, setPayrollData] = useState<CompanyPayrollResponse>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [payrollLoading, setPayrollLoading] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>("timesheet");
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [statusFilter, setStatusFilter] = useState<'active' | 'passive' | 'resigned' | 'all'>('active');
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().startOf("month"),
    dayjs().endOf("month"),
  ]);
  const { t } = useTranslation();
  const { companyId } = useSelector((state: RootState) => state.auth);

  const filteredTimesheetData = useMemo(() => {
    if (statusFilter === 'all') {
      return timesheetData;
    }
    return timesheetData.filter(user => user.userStatus === statusFilter);
  }, [timesheetData, statusFilter]);

  const filteredPayrollData = useMemo(() => {
    if (statusFilter === 'all') {
      return payrollData;
    }
    return payrollData.filter(user => user.status === statusFilter);
  }, [payrollData, statusFilter]);

  // Saat formatını '9 saat 15 dakika' şeklinde göster
  const formatTime = useCallback((hours: number) => {
    const totalMinutes = Math.round(hours * 60);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;

    if (h > 0 && m > 0) {
      return `${h} saat ${m} dakika`;
    } else if (h > 0) {
      return `${h} saat`;
    } else if (m > 0) {
      return `${m} dakika`;
    } else {
      return "0 dakika";
    }
  }, []);

  const formatMinutes = (totalMinutes: number) => {
    return Math.round(totalMinutes).toString();
  };

  const formatHoursAndMinutes = (totalMinutes: number) => {
    const sign = totalMinutes < 0 ? "-" : "";
    const abs = Math.abs(totalMinutes);

    const h = Math.floor(abs / 60);
    const m = abs % 60;

    if (h > 0 && m > 0) return `${sign}${h} saat ${m} dakika`;
    if (h > 0) return `${sign}${h} saat`;
    return `${sign}${m} dakika`;
  };

  // Para formatı fonksiyonu
  const formatCurrency = (value: number | undefined) =>
    new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(value || 0);

  // Bordro verilerini getiren fonksiyon
  const fetchPayrollData = useCallback(async () => {
    if (!companyId) return;
    setPayrollLoading(true);
    try {
      const year = dateRange[0].year();
      const month = dateRange[0].month() + 1; // dayjs month is 0-indexed
      const data = await payrollService.getCompanyPayroll(Number(companyId), year, month);
      setPayrollData(data || []);
    } catch (error) {
      message.error('Bordro verileri alınırken bir hata oluştu.');
      setPayrollData([]);
    } finally {
      setPayrollLoading(false);
    }
  }, [companyId, dateRange]);

  // Filtrelenmiş puantaj verisi


  // Bordro istatistikleri hesaplama
  const payrollTotals = useMemo(() => {
    const totalEmployees = payrollData?.length || 0;
    const totalBaseNet = payrollData?.reduce((sum, item) => sum + (item.baseNetSalary || 0), 0);
    const totalAdditions = payrollData?.reduce((sum, item) => sum + (item.totalAdditions || 0), 0);
    const totalDeductions = payrollData?.reduce((sum, item) => sum + (item.totalDeductions || 0), 0);
    const totalFinalPayable = payrollData?.reduce((sum, item) => sum + (item.finalPayableAmount || 0), 0);
    const avgFinalPayable = totalEmployees > 0 ? totalFinalPayable / totalEmployees : 0;
    return { totalEmployees, totalBaseNet, totalAdditions, totalDeductions, totalFinalPayable, avgFinalPayable };
  }, [payrollData]);

  // İzin türleri etiketleri - Export fonksiyonlarından önce tanımlanmalı
  const leaveTypesLabels: Record<string, string> = {
    casual: "Günlük İzin",
    annual: "Yıllık İzin",
    hourly: "Saatlik İzin",
    medical: "Sağlık İzni",
    marriage: "Evlilik İzni",
    birth: "Doğum İzni",
    unpaid: "Ücretsiz İzin",
    personal: "Kişisel İzin",
    special: "Özel İzin",
    paternity: "Babalık İzni",
    death: "Ölüm İzni",
  };

  // Tüm kullanıcılar için detaylı PDF içeriği oluştur
  const generateAllUsersPrintContent = useCallback(() => {
    const reportTitle = `Puantaj Raporu - ${dateRange[0].format(
      "DD/MM/YYYY"
    )} - ${dateRange[1].format("DD/MM/YYYY")}`;

    // Özet istatistikler
    const totalUsers = filteredTimesheetData.length;
    const activeUsers = filteredTimesheetData.filter(
      (user) => user.netWorkingDays > 0
    ).length;
    const totalNetWorkHours = filteredTimesheetData.reduce(
      (sum, user) =>
        sum +
        user.dailyRecords.reduce(
          (userSum, record) => userSum + (record.netWorkTime || 0),
          0
        ),
      0
    );
    const lateUsers = filteredTimesheetData.filter((user) => user.lateDays > 0).length;

    // Her kullanıcı için detaylı içerik
    const usersContent = filteredTimesheetData
      .map((user) => {
        const workingDays = user.dailyRecords.filter(
          (record) => record.entryTime && record.exitTime
        ).length;
        const attendanceRate =
          user.expectedWorkingDays > 0 ? user.attendanceRate || (workingDays / user.expectedWorkingDays) * 100 : 0;
        const totalLateMinutes = user.dailyRecords.reduce(
          (total, record) => total + (record.lateDuration || 0),
          0
        );
        const totalBreakMinutes = Math.round((user.totalBreakHours || 0) * 60);

        const dailyRecordsRows = user.dailyRecords
          .map(
            (record) => `
        <tr>
          <td>${dayjs(record.date).format("DD.MM.YYYY")}</td>
          <td>${record.entryTime || "-"}</td>
          <td>${record.exitTime || "-"}</td>
          <td>${formatTime(record.totalWorkHours)}</td>
          <td>${formatMinutes((record.totalBreakTime || 0) * 60)} dk</td>
          <td>${formatTime(record.netWorkTime)}</td>
          <td>${record.overtime?.duration
                ? formatTime(record.overtime.duration)
                : "-"
              }</td>
          <td>${!record.entryTime && !record.exitTime
                ? "İzinli"
                : record.isLate
                  ? "Geç (+" + record.lateDuration + " dk)"
                  : "Zamanında"
              }</td>
        </tr>
      `
          )
          .join("");

        return `
        <div class="user-section" style="margin-bottom: 40px; page-break-inside: avoid;">
          <div class="user-header" style="background: #f8f9fa; padding: 15px; border-radius: 8px; margin-bottom: 20px;">
            <h3 style="margin: 0; color: #1890ff;">${user.userName}</h3>
            <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; margin-top: 10px; font-size: 14px;">
              <div><strong>Pozisyon:</strong> ${user.position}</div>
              <div><strong>Departman:</strong> ${user.departmentName}</div>
              <div><strong>Şube:</strong> ${user.branchName}</div>
              <div><strong>E-mail:</strong> ${user.userEmail}</div>
            </div>
          </div>
          
          <div class="user-stats" style="display: grid; grid-template-columns: repeat(5, 1fr); gap: 15px; margin-bottom: 20px;">
            <div class="stat-card" style="border: 1px solid #ddd; padding: 10px; text-align: center; border-radius: 5px;">
              <div style="font-size: 11px; color: #666; margin-bottom: 5px;">Çalışma Günü</div>
              <div style="font-size: 16px; font-weight: bold; color: #0369a1;">${workingDays} / ${user.expectedWorkingDays
          }</div>
            </div>
            <div class="stat-card" style="border: 1px solid #ddd; padding: 10px; text-align: center; border-radius: 5px;">
              <div style="font-size: 11px; color: #666; margin-bottom: 5px;">Toplam Çalışma</div>
              <div style="font-size: 16px; font-weight: bold; color: #16a34a;">${formatTime(
            user.totalWorkingHours
          )}</div>
            </div>
            <div class="stat-card" style="border: 1px solid #ddd; padding: 10px; text-align: center; border-radius: 5px;">
              <div style="font-size: 11px; color: #666; margin-bottom: 5px;">Devam Oranı</div>
              <div style="font-size: 16px; font-weight: bold; color: #ca8a04;">${attendanceRate.toFixed(
            1
          )}%</div>
            </div>
            <div class="stat-card" style="border: 1px solid #ddd; padding: 10px; text-align: center; border-radius: 5px;">
              <div style="font-size: 11px; color: #666; margin-bottom: 5px;">Geç Kalma</div>
              <div style="font-size: 16px; font-weight: bold; color: ${totalLateMinutes > 0 ? "#dc2626" : "#16a34a"
          };">${totalLateMinutes} dk</div>
            </div>
            <div class="stat-card" style="border: 1px solid #ddd; padding: 10px; text-align: center; border-radius: 5px;">
              <div style="font-size: 11px; color: #666; margin-bottom: 5px;">Toplam Mola</div>
              <div style="font-size: 16px; font-weight: bold; color: #6b7280;">${totalBreakMinutes} dk</div>
            </div>
          </div>

          <h4 style="color: #1890ff; margin-bottom: 15px;">Günlük Çalışma Detayları</h4>
          <table style="width: 100%; border-collapse: collapse; margin-bottom: 30px; font-size: 12px;">
            <thead>
              <tr style="background-color: #f5f5f5;">
                <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Tarih</th>
                <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Giriş</th>
                <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Çıkış</th>
            <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Mola</th>
            <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Net Çalışma</th>
            <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Fazla/Eksik Mesai</th>
            <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Durum</th>
            <th style="border: 1px solid #ddd; padding: 8px; text-align: left;"></th>

              </tr>
            </thead>
            <tbody>
              ${dailyRecordsRows}
            </tbody>
          </table>
        </div>
      `;
      })
      .join("");

    return `
      <div class="report-header" style="text-align: center; margin-bottom: 40px; border-bottom: 2px solid #1890ff; padding-bottom: 20px;">
        <h1 style="color: #1890ff; margin-bottom: 10px;">${reportTitle}</h1>
        <div style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; margin-top: 30px;">
          <div style="text-align: center;">
            <div style="font-size: 24px; font-weight: bold; color: #0369a1;">${totalUsers}</div>
            <div style="font-size: 14px; color: #666;">Toplam Çalışan</div>
          </div>
          <div style="text-align: center;">
            <div style="font-size: 24px; font-weight: bold; color: #16a34a;">${activeUsers}</div>
            <div style="font-size: 14px; color: #666;">Aktif Çalışan</div>
          </div>
          <div style="text-align: center;">
            <div style="font-size: 24px; font-weight: bold; color: #ca8a04;">${formatTime(
      totalNetWorkHours
    )}</div>
            <div style="font-size: 14px; color: #666;">Net Çalışma</div>
          </div>
          <div style="text-align: center;">
            <div style="font-size: 24px; font-weight: bold; color: #dc2626;">${lateUsers}</div>
            <div style="font-size: 14px; color: #666;">Geç Kalan Çalışan</div>
          </div>
        </div>
      </div>
      
      ${usersContent}
    `;
  }, [filteredTimesheetData, dateRange, formatTime]);

  // ========== PUANTAJ RAPORU EXPORT FONKSİYONLARI ==========
  // Tüm çalışanlar için Puantaj PDF Export
  const getRecordsInSelectedRange = useCallback(
    (records: DailyRecord[]) => {
      const selectedStartDate = dateRange[0].format("YYYY-MM-DD");
      const selectedEndDate = dateRange[1].format("YYYY-MM-DD");

      return records.filter((record) => {
        const recordDate = dayjs(record.date).format("YYYY-MM-DD");

        return (
          recordDate >= selectedStartDate &&
          recordDate <= selectedEndDate
        );
      });
    },
    [dateRange],
  );

  const getTimesheetRecordStatus = useCallback(
    (record: DailyRecord) => {
      if (
        record.leaveStatus &&
        record.leaveStatus !== "hourly (approved)"
      ) {
        const leaveType = record.leaveDetail?.type || "Bilinmeyen";
        return `İzinli: ${leaveTypesLabels[leaveType] || leaveType}`;
      }

      if (record.entryTime && record.exitTime) {
        if (record.status?.length) {
          const statusText = record.status
            .filter((status) => status !== "Erken Çıktı")
            .join(", ");

          return (
            statusText ||
            (record.isLate
              ? `Geç (+${record.lateDuration || 0} dk)`
              : "Zamanında")
          );
        }

        return record.isLate
          ? `Geç (+${record.lateDuration || 0} dk)`
          : "Zamanında";
      }

      if (!record.entryTime && !record.exitTime && !record.leaveStatus) {
        return "Kayıt Yok";
      }

      return "-";
    },
    [leaveTypesLabels],
  );

  // Backend mazeretleri string olarak gönderir; eski formatta { reason } nesnesi de gelebilir
  const getTimesheetExcuses = (record: DailyRecord) => {
    const reasons = (record.excuses || [])
      .map((excuse: any) => (typeof excuse === "string" ? excuse : excuse?.reason))
      .filter(Boolean);
    return reasons.length ? normalizeTurkishChars(reasons.join(", ")) : "-";
  };

  const getTimesheetFieldSessions = (record: DailyRecord) => {
    const sessions = record.fieldSessions || [];
    if (!sessions.length) return "-";
    return normalizeTurkishChars(
      sessions
        .map((session) => {
          const range = `${session.startTime || "?"} - ${session.endTime || (session.isOngoing ? "Devam ediyor" : "?")}`;
          return session.activityTitle ? `${range} (${session.activityTitle})` : range;
        })
        .join(", "),
    );
  };

  const getTimesheetOvertime = useCallback(
    (record: DailyRecord) =>
      record.overtime?.duration && record.overtime.duration > 0
        ? `${Math.round(record.overtime.duration)} dakika`
        : "-",
    [],
  );

  // ============================================================
  // TÜM ÇALIŞANLAR PUANTAJ PDF
  // ============================================================
  const exportAllTimesheetToPDF = useCallback(async () => {
    try {
      if (!filteredTimesheetData?.length) {
        message.warning("İndirilecek veri bulunamadı.");
        return;
      }

      const pdfData: any[] = [];

      filteredTimesheetData.forEach((user) => {
        const selectedRecords = getRecordsInSelectedRange(
          user.dailyRecords,
        );

        selectedRecords.forEach((record) => {
          const dayName = dayjs(record.date)
            .locale("tr")
            .format("dddd");

          const status = getTimesheetRecordStatus(record);
          const overtime = getTimesheetOvertime(record);

          pdfData.push({
            "Ad Soyad": normalizeTurkishChars(user.userName || "-"),
            Departman: normalizeTurkishChars(
              user.departmentName || "-",
            ),
            Şube: normalizeTurkishChars(user.branchName || "-"),
            Tarih: dayjs(record.date).format("DD.MM.YYYY"),
            Gün: normalizeTurkishChars(dayName),
            "Giriş Saati": record.entryTime || "-",
            "Çıkış Saati": record.exitTime || "-",
            "Net Çalışma":
              record.totalWorkHours > 0
                ? formatTime(record.totalWorkHours)
                : "-",
            "Mola (dk)":
              (record.totalBreakTime || 0) > 0
                ? formatMinutes((record.totalBreakTime || 0) * 60)
                : "-",
            Mesai: overtime,
            Durum: normalizeTurkishChars(status),
            Mazeret: getTimesheetExcuses(record),
            Saha: getTimesheetFieldSessions(record),
          });
        });
      });

      if (pdfData.length === 0) {
        message.warning("Seçilen tarih aralığında kayıt bulunamadı.");
        return;
      }

      const reportTitle =
        `PUANTAJ RAPORU - ` +
        `${dateRange[0].format("DD/MM/YYYY")} - ` +
        `${dateRange[1].format("DD/MM/YYYY")}`;

      const columns = [
        {
          header: normalizeTurkishChars("Ad Soyad"),
          property: "Ad Soyad",
          width: 32,
          align: "left" as const,
        },
        {
          header: normalizeTurkishChars("Departman"),
          property: "Departman",
          width: 28,
          align: "left" as const,
        },
        {
          header: normalizeTurkishChars("Şube"),
          property: "Şube",
          width: 24,
          align: "left" as const,
        },
        {
          header: normalizeTurkishChars("Tarih"),
          property: "Tarih",
          width: 24,
          align: "center" as const,
        },
        {
          header: normalizeTurkishChars("Gün"),
          property: "Gün",
          width: 24,
          align: "left" as const,
        },
        {
          header: normalizeTurkishChars("Giriş Saati"),
          property: "Giriş Saati",
          width: 24,
          align: "center" as const,
        },
        {
          header: normalizeTurkishChars("Çıkış Saati"),
          property: "Çıkış Saati",
          width: 24,
          align: "center" as const,
        },
        {
          header: normalizeTurkishChars("Net Çalışma"),
          property: "Net Çalışma",
          width: 24,
          align: "center" as const,
        },
        {
          header: normalizeTurkishChars("Mola (dk)"),
          property: "Mola (dk)",
          width: 20,
          align: "center" as const,
        },
        {
          header: normalizeTurkishChars("Mesai"),
          property: "Mesai",
          width: 20,
          align: "center" as const,
        },
        {
          header: normalizeTurkishChars("Durum"),
          property: "Durum",
          width: 28,
          align: "left" as const,
        },
        {
          header: normalizeTurkishChars("Mazeret"),
          property: "Mazeret",
          width: 30,
          align: "left" as const,
        },
        {
          header: normalizeTurkishChars("Saha"),
          property: "Saha",
          width: 30,
          align: "left" as const,
        },
      ];

      const selectedUsers = filteredTimesheetData.filter(
        (user) =>
          getRecordsInSelectedRange(user.dailyRecords).length > 0,
      );

      const totalNetWorkHours = selectedUsers.reduce(
        (total, user) =>
          total +
          getRecordsInSelectedRange(user.dailyRecords).reduce(
            (userTotal, record) =>
              userTotal + (record.netWorkTime || 0),
            0,
          ),
        0,
      );

      const activeUsers = selectedUsers.filter((user) =>
        getRecordsInSelectedRange(user.dailyRecords).some(
          (record) => record.totalWorkHours > 0,
        ),
      ).length;

      const lateUsers = selectedUsers.filter((user) =>
        getRecordsInSelectedRange(user.dailyRecords).some(
          (record) => record.isLate,
        ),
      ).length;

      const statistics = [
        {
          label: normalizeTurkishChars("Toplam Çalışan"),
          value: selectedUsers.length,
        },
        {
          label: normalizeTurkishChars("Aktif Çalışan"),
          value: activeUsers,
        },
        {
          label: normalizeTurkishChars("Net Çalışma"),
          value: formatTime(totalNetWorkHours),
        },
        {
          label: normalizeTurkishChars("Geç Kalan"),
          value: lateUsers,
        },
      ];

      await createTimesheetPayrollPdf(pdfData, {
        title: normalizeTurkishChars(reportTitle),
        description: normalizeTurkishChars(
          `Puantaj raporu - ${dateRange[0].format(
            "DD/MM/YYYY",
          )} - ${dateRange[1].format("DD/MM/YYYY")}`,
        ),
        columns: columns as any,
        showStatistics: true,
        statistics,
        headerColor: "#1890ff",
        footer: {
          text: normalizeTurkishChars(
            `© ${new Date().getFullYear()} DINOX - Tüm hakları saklıdır.`,
          ),
          showPageNumbers: true,
        },
      });

      message.success("PDF dosyası başarıyla indirildi.");
    } catch (error) {
      console.error("Tüm çalışanlar PDF export hatası:", error);
      message.error("PDF export sırasında hata oluştu.");
    }
  }, [
    filteredTimesheetData,
    dateRange,
    formatTime,
    getRecordsInSelectedRange,
    getTimesheetRecordStatus,
    getTimesheetOvertime,
  ]);

  // ============================================================
  // BİREYSEL PUANTAJ PDF
  // ============================================================
  const exportUserTimesheetToPDF = useCallback(
    async (user: TimesheetUserData) => {
      try {
        const selectedRecords = getRecordsInSelectedRange(
          user.dailyRecords,
        );

        if (selectedRecords.length === 0) {
          message.warning("Seçilen tarih aralığında kayıt bulunamadı.");
          return;
        }

        const pdfData = selectedRecords.map((record) => {
          const dayName = dayjs(record.date)
            .locale("tr")
            .format("dddd");

          return {
            Tarih: dayjs(record.date).format("DD.MM.YYYY"),
            Gün: normalizeTurkishChars(dayName),
            "Giriş Saati": record.entryTime || "-",
            "Çıkış Saati": record.exitTime || "-",
            "Net Çalışma":
              record.totalWorkHours > 0
                ? formatTime(record.totalWorkHours)
                : "-",
            "Mola (dk)":
              (record.totalBreakTime || 0) > 0
                ? formatMinutes((record.totalBreakTime || 0) * 60)
                : "-",
            Mesai: getTimesheetOvertime(record),
            Durum: normalizeTurkishChars(
              getTimesheetRecordStatus(record),
            ),
            Mazeret: getTimesheetExcuses(record),
            Saha: getTimesheetFieldSessions(record),
          };
        });

        const reportTitle = normalizeTurkishChars(
          `${user.userName} - Puantaj Raporu - ` +
          `${dateRange[0].format("DD/MM/YYYY")} - ` +
          `${dateRange[1].format("DD/MM/YYYY")}`,
        );

        const columns = [
          {
            header: normalizeTurkishChars("Tarih"),
            property: "Tarih",
            width: 30,
            align: "center" as const,
          },
          {
            header: normalizeTurkishChars("Gün"),
            property: "Gün",
            width: 35,
            align: "left" as const,
          },
          {
            header: normalizeTurkishChars("Giriş Saati"),
            property: "Giriş Saati",
            width: 30,
            align: "center" as const,
          },
          {
            header: normalizeTurkishChars("Çıkış Saati"),
            property: "Çıkış Saati",
            width: 30,
            align: "center" as const,
          },
          {
            header: normalizeTurkishChars("Net Çalışma"),
            property: "Net Çalışma",
            width: 35,
            align: "center" as const,
          },
          {
            header: normalizeTurkishChars("Mola (dk)"),
            property: "Mola (dk)",
            width: 25,
            align: "center" as const,
          },
          {
            header: normalizeTurkishChars("Mesai"),
            property: "Mesai",
            width: 25,
            align: "center" as const,
          },
          {
            header: normalizeTurkishChars("Durum"),
            property: "Durum",
            width: 50,
            align: "left" as const,
          },
          {
            header: normalizeTurkishChars("Mazeret"),
            property: "Mazeret",
            width: 45,
            align: "left" as const,
          },
          {
            header: normalizeTurkishChars("Saha"),
            property: "Saha",
            width: 45,
            align: "left" as const,
          },
        ];

        console.log("Bireysel PDF tarih kontrolü:", {
          selectedStartDate: dateRange[0].format("YYYY-MM-DD"),
          selectedEndDate: dateRange[1].format("YYYY-MM-DD"),
          exportedDates: pdfData.map((row) => row.Tarih),
        });

        await createTimesheetPayrollPdf(pdfData, {
          title: reportTitle,
          description: normalizeTurkishChars(
            `${user.userName} puantaj raporu`,
          ),
          columns: columns as any,
          headerColor: "#1890ff",
          footer: {
            text: normalizeTurkishChars(
              `© ${new Date().getFullYear()} DINOX - Tüm hakları saklıdır.`,
            ),
            showPageNumbers: true,
          },
        });

        message.success(
          `${user.userName} için PDF dosyası başarıyla indirildi.`,
        );
      } catch (error) {
        console.error("Bireysel PDF export hatası:", error);
        message.error("PDF export sırasında hata oluştu.");
      }
    },
    [
      dateRange,
      formatTime,
      getRecordsInSelectedRange,
      getTimesheetRecordStatus,
      getTimesheetOvertime,
    ],
  );

  // ============================================================
  // TÜM ÇALIŞANLAR PUANTAJ EXCEL
  // ============================================================
  const exportAllTimesheetToExcel = useCallback(() => {
    try {
      if (!filteredTimesheetData?.length) {
        message.warning("İndirilecek veri bulunamadı.");
        return;
      }

      const excelData: any[] = [];

      filteredTimesheetData.forEach((user) => {
        const selectedRecords = getRecordsInSelectedRange(
          user.dailyRecords,
        );

        selectedRecords.forEach((record) => {
          excelData.push({
            "Ad Soyad": user.userName,
            Departman: user.departmentName,
            Şube: user.branchName,
            Tarih: dayjs(record.date).format("DD.MM.YYYY"),
            Gün: dayjs(record.date).locale("tr").format("dddd"),
            "Giriş Saati": record.entryTime || "-",
            "Çıkış Saati": record.exitTime || "-",
            "Net Çalışma":
              record.totalWorkHours > 0
                ? formatTime(record.totalWorkHours)
                : "-",
            "Mola (dk)":
              (record.totalBreakTime || 0) > 0
                ? formatMinutes((record.totalBreakTime || 0) * 60)
                : "-",
            Mesai: getTimesheetOvertime(record),
            Durum: getTimesheetRecordStatus(record),
          });
        });
      });

      if (excelData.length === 0) {
        message.warning("Seçilen tarih aralığında kayıt bulunamadı.");
        return;
      }

      const reportTitle =
        `PUANTAJ_RAPORU_` +
        `${dateRange[0].format("DD_MM_YYYY")}_` +
        `${dateRange[1].format("DD_MM_YYYY")}`;

      const columns = [
        {
          header: "Ad Soyad",
          property: "Ad Soyad" as keyof (typeof excelData)[0],
          width: 20,
        },
        {
          header: "Departman",
          property: "Departman" as keyof (typeof excelData)[0],
          width: 20,
        },
        {
          header: "Şube",
          property: "Şube" as keyof (typeof excelData)[0],
          width: 15,
        },
        {
          header: "Tarih",
          property: "Tarih" as keyof (typeof excelData)[0],
          width: 12,
        },
        {
          header: "Gün",
          property: "Gün" as keyof (typeof excelData)[0],
          width: 15,
        },
        {
          header: "Giriş Saati",
          property: "Giriş Saati" as keyof (typeof excelData)[0],
          width: 12,
        },
        {
          header: "Çıkış Saati",
          property: "Çıkış Saati" as keyof (typeof excelData)[0],
          width: 12,
        },
        {
          header: "Net Çalışma",
          property: "Net Çalışma" as keyof (typeof excelData)[0],
          width: 15,
        },
        {
          header: "Mola (dk)",
          property: "Mola (dk)" as keyof (typeof excelData)[0],
          width: 12,
        },
        {
          header: "Mesai",
          property: "Mesai" as keyof (typeof excelData)[0],
          width: 12,
        },
        {
          header: "Durum",
          property: "Durum" as keyof (typeof excelData)[0],
          width: 20,
        },
      ];

      exportToExcelUtil(excelData, reportTitle, columns);
      message.success("Excel dosyası başarıyla indirildi.");
    } catch (error) {
      console.error("Tüm çalışanlar Excel export hatası:", error);
      message.error("Excel export sırasında hata oluştu.");
    }
  }, [
    filteredTimesheetData,
    dateRange,
    formatTime,
    getRecordsInSelectedRange,
    getTimesheetRecordStatus,
    getTimesheetOvertime,
  ]);

  // ============================================================
  // BİREYSEL PUANTAJ EXCEL
  // ============================================================
  const exportUserTimesheetToExcel = useCallback(
    (user: TimesheetUserData) => {
      try {
        const selectedRecords = getRecordsInSelectedRange(
          user.dailyRecords,
        );

        if (selectedRecords.length === 0) {
          message.warning("Seçilen tarih aralığında kayıt bulunamadı.");
          return;
        }

        const excelData = selectedRecords.map((record) => ({
          Tarih: dayjs(record.date).format("DD.MM.YYYY"),
          Gün: dayjs(record.date).locale("tr").format("dddd"),
          "Giriş Saati": record.entryTime || "-",
          "Çıkış Saati": record.exitTime || "-",
          "Net Çalışma":
            record.totalWorkHours > 0
              ? formatTime(record.totalWorkHours)
              : "-",
          "Mola (dk)":
            (record.totalBreakTime || 0) > 0
              ? formatMinutes((record.totalBreakTime || 0) * 60)
              : "-",
          Mesai: getTimesheetOvertime(record),
          Durum: getTimesheetRecordStatus(record),
        }));

        const reportTitle =
          `${user.userName}_PUANTAJ_` +
          `${dateRange[0].format("DD_MM_YYYY")}_` +
          `${dateRange[1].format("DD_MM_YYYY")}`;

        const columns = [
          {
            header: "Tarih",
            property: "Tarih" as keyof (typeof excelData)[0],
            width: 12,
          },
          {
            header: "Gün",
            property: "Gün" as keyof (typeof excelData)[0],
            width: 15,
          },
          {
            header: "Giriş Saati",
            property: "Giriş Saati" as keyof (typeof excelData)[0],
            width: 12,
          },
          {
            header: "Çıkış Saati",
            property: "Çıkış Saati" as keyof (typeof excelData)[0],
            width: 12,
          },
          {
            header: "Net Çalışma",
            property: "Net Çalışma" as keyof (typeof excelData)[0],
            width: 15,
          },
          {
            header: "Mola (dk)",
            property: "Mola (dk)" as keyof (typeof excelData)[0],
            width: 12,
          },
          {
            header: "Mesai",
            property: "Mesai" as keyof (typeof excelData)[0],
            width: 12,
          },
          {
            header: "Durum",
            property: "Durum" as keyof (typeof excelData)[0],
            width: 20,
          },
        ];

        console.log("Bireysel Excel tarih kontrolü:", {
          selectedStartDate: dateRange[0].format("YYYY-MM-DD"),
          selectedEndDate: dateRange[1].format("YYYY-MM-DD"),
          exportedDates: excelData.map((row) => row.Tarih),
        });

        exportToExcelUtil(excelData, reportTitle, columns);
        message.success(
          `${user.userName} için Excel dosyası başarıyla indirildi.`,
        );
      } catch (error) {
        console.error("Bireysel Excel export hatası:", error);
        message.error("Excel export sırasında hata oluştu.");
      }
    },
    [
      dateRange,
      formatTime,
      getRecordsInSelectedRange,
      getTimesheetRecordStatus,
      getTimesheetOvertime,
    ],
  );

  // ========== MAAŞ RAPORU EXPORT FONKSİYONLARI ==========

  // Tüm çalışanlar için Maaş PDF Export
  const exportAllPayrollToPDF = useCallback(async () => {
    try {
      if (!filteredPayrollData || filteredPayrollData.length === 0) {
        message.warning("İndirilecek veri bulunamadı.");
        return;
      }

      const pdfData: any[] = filteredPayrollData.map((item) => ({
        "Ad Soyad": normalizeTurkishChars(`${item.firstName} ${item.lastName}`),
        "Departman": normalizeTurkishChars(item.department || "-"),
        "Temel Net Maaş": formatCurrency(item.baseNetSalary),
        "Toplam Eklemeler": formatCurrency(item.totalAdditions),
        "Toplam Kesintiler": formatCurrency(item.totalDeductions),
        "Ödenecek Tutar": formatCurrency(item.finalPayableAmount),
        "Durum": normalizeTurkishChars(item.status || "-"),
      }));

      const reportTitle = normalizeTurkishChars(`MAAS RAPORU - ${dateRange[0].format("MMMM YYYY")}`);

      const columns = [
        { header: normalizeTurkishChars("Ad Soyad"), property: "Ad Soyad", width: 40, align: 'left' as const },
        { header: normalizeTurkishChars("Departman"), property: "Departman", width: 35, align: 'left' as const },
        { header: normalizeTurkishChars("Temel Net Maaş"), property: "Temel Net Maaş", width: 35, align: 'right' as const },
        { header: normalizeTurkishChars("Toplam Eklemeler"), property: "Toplam Eklemeler", width: 35, align: 'right' as const },
        { header: normalizeTurkishChars("Toplam Kesintiler"), property: "Toplam Kesintiler", width: 35, align: 'right' as const },
        { header: normalizeTurkishChars("Ödenecek Tutar"), property: "Ödenecek Tutar", width: 35, align: 'right' as const },
        { header: normalizeTurkishChars("Durum"), property: "Durum", width: 30, align: 'center' as const },
      ];

      const statistics = [
        { label: normalizeTurkishChars('Toplam Çalışan'), value: payrollTotals.totalEmployees },
        { label: normalizeTurkishChars('Toplam Ödeme'), value: formatCurrency(payrollTotals.totalFinalPayable) },
        { label: normalizeTurkishChars('Ortalama Ödeme'), value: formatCurrency(payrollTotals.avgFinalPayable) },
        { label: normalizeTurkishChars('Toplam Kesinti'), value: formatCurrency(payrollTotals.totalDeductions) },
      ];

      await createTimesheetPayrollPdf(pdfData, {
        title: normalizeTurkishChars(reportTitle),
        description: normalizeTurkishChars(`Maaş raporu - ${dateRange[0].format("MMMM YYYY")}`),
        columns: columns as any,
        showStatistics: true,
        statistics: statistics,
        headerColor: '#52c41a',
        footer: {
          text: normalizeTurkishChars(`© ${new Date().getFullYear()} DINOX - Tüm hakları saklıdır.`),
          showPageNumbers: true,
        },
      });

      message.success("PDF dosyası başarıyla indirildi.");
    } catch (error) {
      message.error("PDF export sırasında hata oluştu.");
    }
  }, [filteredPayrollData, dateRange, payrollTotals, formatCurrency]);

  // Bireysel Maaş PDF Export
  const exportUserPayrollToPDF = useCallback(
    (user: CompanyPayrollItem) => {
      try {
        const pdfData: any[] = [];

        // Temel bilgiler
        pdfData.push({
          "Kategori": "Çalışan Bilgileri",
          "Değer": `${user.firstName} ${user.lastName}`,
        });
        pdfData.push({
          "Kategori": "Departman",
          "Değer": user.department || "-",
        });
        pdfData.push({
          "Kategori": "Temel Net Maaş",
          "Değer": formatCurrency(user.baseNetSalary),
        });

        // Eklemeler ve Kesintiler - CalculationBreakdown detayları yok, sadece toplamları göster
        pdfData.push({ "Kategori": "", "Değer": "" });
        pdfData.push({ "Kategori": "EKLEMELER", "Değer": formatCurrency(user.totalAdditions) });
        pdfData.push({ "Kategori": "KESİNTİLER", "Değer": formatCurrency(user.totalDeductions) });

        pdfData.push({ "Kategori": "", "Değer": "" });
        pdfData.push({
          "Kategori": "ÖDENECEK TUTAR",
          "Değer": formatCurrency(user.finalPayableAmount),
        });

        const reportTitle = `${user.firstName} ${user.lastName} - Maaş Raporu - ${dateRange[0].format("MMMM YYYY")}`;

        const columns = [
          { header: "Kategori", property: "Kategori", width: 50 },
          { header: "Değer", property: "Değer", width: 50 },
        ];

        createTimesheetPayrollPdf(pdfData, {
          title: reportTitle,
          description: `${user.firstName} ${user.lastName} maaş raporu`,
          columns: columns as any,
          headerColor: '#52c41a',
          footer: {
            text: `© ${new Date().getFullYear()} DINOX - Tüm hakları saklıdır.`,
            showPageNumbers: true,
          },
        });

        message.success(`${user.firstName} ${user.lastName} için PDF dosyası başarıyla indirildi.`);
      } catch (error) {
        message.error("PDF export sırasında hata oluştu.");
      }
    },
    [dateRange, formatCurrency]
  );

  // Tüm çalışanlar için Maaş Excel Export
  const exportAllPayrollToExcel = useCallback(() => {
    try {
      if (!filteredPayrollData || filteredPayrollData.length === 0) {
        message.warning("İndirilecek veri bulunamadı.");
        return;
      }

      const excelData: any[] = filteredPayrollData.map((item) => ({
        "Ad Soyad": `${item.firstName} ${item.lastName}`,
        "Departman": item.department || "-",
        "Temel Net Maaş": item.baseNetSalary || 0,
        "Toplam Eklemeler": item.totalAdditions || 0,
        "Toplam Kesintiler": item.totalDeductions || 0,
        "Ödenecek Tutar": item.finalPayableAmount || 0,
        "Durum": item.status || "-",
      }));

      const reportTitle = `MAAS_RAPORU_${dateRange[0].format("MMMM_YYYY")}`;

      const columns = [
        { header: "Ad Soyad", property: "Ad Soyad" as keyof typeof excelData[0], width: 25 },
        { header: "Departman", property: "Departman" as keyof typeof excelData[0], width: 20 },
        { header: "Temel Net Maaş", property: "Temel Net Maaş" as keyof typeof excelData[0], width: 18 },
        { header: "Toplam Eklemeler", property: "Toplam Eklemeler" as keyof typeof excelData[0], width: 18 },
        { header: "Toplam Kesintiler", property: "Toplam Kesintiler" as keyof typeof excelData[0], width: 18 },
        { header: "Ödenecek Tutar", property: "Ödenecek Tutar" as keyof typeof excelData[0], width: 18 },
        { header: "Durum", property: "Durum" as keyof typeof excelData[0], width: 15 },
      ];

      exportToExcelUtil(excelData, reportTitle, columns);
      message.success("Excel dosyası başarıyla indirildi.");
    } catch (error) {
      message.error("Excel export sırasında hata oluştu.");
    }
  }, [filteredPayrollData, dateRange]);

  // Bireysel Maaş Excel Export
  const exportUserPayrollToExcel = useCallback(
    (user: CompanyPayrollItem) => {
      try {
        const excelData: any[] = [];

        // Temel bilgiler
        excelData.push({
          "Kategori": "Çalışan Bilgileri",
          "Değer": `${user.firstName} ${user.lastName}`,
        });
        excelData.push({
          "Kategori": "Departman",
          "Değer": user.department || "-",
        });
        excelData.push({
          "Kategori": "Temel Net Maaş",
          "Değer": user.baseNetSalary || 0,
        });

        // Eklemeler ve Kesintiler - CalculationBreakdown detayları yok, sadece toplamları göster
        excelData.push({ "Kategori": "", "Değer": "" });
        excelData.push({ "Kategori": "EKLEMELER", "Değer": user.totalAdditions || 0 });
        excelData.push({ "Kategori": "KESİNTİLER", "Değer": user.totalDeductions || 0 });

        excelData.push({ "Kategori": "", "Değer": "" });
        excelData.push({
          "Kategori": "ÖDENECEK TUTAR",
          "Değer": user.finalPayableAmount || 0,
        });

        const reportTitle = `${user.firstName}_${user.lastName}_MAAS_${dateRange[0].format("MMMM_YYYY")}`;

        const columns = [
          { header: "Kategori", property: "Kategori" as keyof typeof excelData[0], width: 50 },
          { header: "Değer", property: "Değer" as keyof typeof excelData[0], width: 50 },
        ];

        exportToExcelUtil(excelData, reportTitle, columns);
        message.success(`${user.firstName} ${user.lastName} için Excel dosyası başarıyla indirildi.`);
      } catch (error) {
        message.error("Excel export sırasında hata oluştu.");
      }
    },
    [dateRange]
  );

  // ========== ESKİ FONKSİYONLAR (Geriya Uyumluluk İçin Korundu) ==========

  // PDF Export Fonksiyonu - Tüm detaylar ile
  const exportToPDF = useCallback(() => {
    try {
      if (!filteredTimesheetData || filteredTimesheetData.length === 0) {
        message.warning("İndirilecek veri bulunamadı.");
        return;
      }

      const printContent = generateAllUsersPrintContent();
      const printWindow = window.open("", "_blank");

      if (printWindow) {
        const htmlContent = `
          <html>
            <head>
              <title>Puantaj Raporu - ${dateRange[0].format(
          "DD-MM-YYYY"
        )} - ${dateRange[1].format("DD-MM-YYYY")}</title>
              <style>
                body { 
                  font-family: Arial, sans-serif; 
                  margin: 20px;
                  line-height: 1.4;
                }
                .report-header {
                  margin-bottom: 40px;
                }
                .user-section {
                  margin-bottom: 40px;
                  page-break-inside: avoid;
                }
                .user-header {
                  background: #f8f9fa;
                  padding: 15px;
                  border-radius: 8px;
                  margin-bottom: 20px;
                }
                .user-stats {
                  display: grid;
                  grid-template-columns: repeat(5, 1fr);
                  gap: 15px;
                  margin-bottom: 20px;
                }
                .stat-card {
                  border: 1px solid #ddd;
                  padding: 10px;
                  text-align: center;
                  border-radius: 5px;
                }
                table {
                  width: 100%;
                  border-collapse: collapse;
                  margin-bottom: 30px;
                  font-size: 12px;
                }
                th, td {
                  border: 1px solid #ddd;
                  padding: 8px;
                  text-align: left;
                }
                th {
                  background-color: #f5f5f5;
                  font-weight: bold;
                }
                h1, h2, h3, h4 {
                  margin-top: 0;
                }
                @media print {
                  .user-section {
                    page-break-inside: avoid;
                  }
                  .report-header {
                    page-break-after: auto;
                  }
                }
              </style>
            </head>
            <body>
              ${printContent}
            </body>
          </html>
        `;

        printWindow.document.write(htmlContent);
        printWindow.document.close();
        printWindow.print();
        printWindow.close();
        message.success(
          'Detaylı PDF export başlatıldı. Yazdırma seçeneklerinde "PDF olarak kaydet" seçeneğini kullanın.'
        );
      }
    } catch (error) {
      message.error("PDF export sırasında hata oluştu.");
    }
  }, [filteredTimesheetData, dateRange, generateAllUsersPrintContent]);

  // Excel Export Fonksiyonu - Tüm detaylar ile
  const exportToExcel = useCallback(() => {
    try {
      if (!filteredTimesheetData || filteredTimesheetData.length === 0) {
        message.warning("İndirilecek veri bulunamadı.");
        return;
      }

      // Rapor başlığı ve genel bilgiler
      const reportTitle =
        "PUANTAJ RAPORU - " +
        dateRange[0].format("DD/MM/YYYY") +
        " - " +
        dateRange[1].format("DD/MM/YYYY");
      const totalUsers = filteredTimesheetData.length;
      const activeUsers = filteredTimesheetData.filter(
        (user) => user.netWorkingDays > 0
      ).length;
      const totalNetWorkHours = filteredTimesheetData.reduce(
        (sum, user) =>
          sum +
          user.dailyRecords.reduce(
            (userSum, record) => userSum + (record.netWorkTime || 0),
            0
          ),
        0
      );
      const lateUsers = filteredTimesheetData.filter(
        (user) => user.lateDays > 0
      ).length;

      // CSV content array
      const csvLines = [];

      // Rapor başlığı
      csvLines.push([reportTitle]);
      csvLines.push([""]);

      // Genel istatistikler
      csvLines.push(["GENEL İSTATİSTİKLER"]);
      csvLines.push(["Toplam Çalışan", totalUsers]);
      csvLines.push(["Aktif Çalışan", activeUsers]);
      csvLines.push(["Net Çalışma", formatTime(totalNetWorkHours)]);
      csvLines.push(["Geç Kalan Çalışan", lateUsers]);
      csvLines.push([""]);

      // Her kullanıcı için detaylı veriler
      filteredTimesheetData.forEach((user, index) => {
        const workingDays = user.dailyRecords.filter(
          (record) => record.entryTime && record.exitTime
        ).length;
        const attendanceRate =
          user.expectedWorkingDays > 0 ? user.attendanceRate || (workingDays / user.expectedWorkingDays) * 100 : 0;
        const totalLateMinutes = user.dailyRecords.reduce(
          (total, record) => total + (record.lateDuration || 0),
          0
        );
        const totalBreakMinutes = Math.round((user.totalBreakHours || 0) * 60);
        const userNetWorkHours = user.dailyRecords.reduce(
          (total, record) => total + (record.netWorkTime || 0),
          0
        );

        // Kullanıcı başlığı
        csvLines.push([index + 1 + ". " + user.userName.toUpperCase()]);
        csvLines.push([""]);

        // Kullanıcı temel bilgileri
        csvLines.push(["KULLANICI BİLGİLERİ"]);
        csvLines.push(["Ad Soyad", user.userName]);
        csvLines.push(["E-mail", user.userEmail]);
        csvLines.push(["Pozisyon", user.position]);
        csvLines.push(["Departman", user.departmentName]);
        csvLines.push(["Şube", user.branchName]);
        csvLines.push([""]);

        // Kullanıcı istatistikleri
        csvLines.push(["İSTATİSTİKLER"]);
        csvLines.push([
          "Çalışma Günü",
          workingDays + " / " + user.expectedWorkingDays,
        ]);
        csvLines.push(["Net Çalışma", formatTime(userNetWorkHours)]);
        csvLines.push(["Devam Oranı", attendanceRate.toFixed(1) + "%"]);
        csvLines.push(["Geç Kalma", totalLateMinutes + " dk"]);
        csvLines.push(["Toplam Mola", totalBreakMinutes + " dk"]);
        csvLines.push([
          "Ortalama Günlük Saat",
          (user.averageDailyHours
            ? user.averageDailyHours.toFixed(2)
            : "0.00") + " saat",
        ]);
        csvLines.push([
          "Toplam Mesai",
          formatTime(user.totalOvertimeHours || 0),
        ]);
        csvLines.push([""]);

        // Günlük kayıtlar tablosu
        csvLines.push(["GÜNLÜK ÇALIŞMA KAYITLARI"]);
        csvLines.push([
          "Tarih",
          "Gün",
          "Giriş Saati",
          "Çıkış Saati",
          "Net Çalışma",
          "Mola (dk)",
          "Fazla/Eksik Mesai",
          "Durum",
          "Geç Kalma (dk)",
        ]);

        user.dailyRecords.forEach((record) => {
          const dayName = dayjs(record.date).format("dddd");
          const status =
            !record.entryTime && !record.exitTime
              ? "İzinli"
              : record.isLate
                ? "Geç"
                : "Zamanında";
          const overtime = record.overtime?.duration
            ? formatTime(record.overtime.duration)
            : "-";

          csvLines.push([
            dayjs(record.date).format("DD.MM.YYYY"),
            dayName,
            record.entryTime || "-",
            record.exitTime || "-",
            formatTime(record.totalWorkHours),
            formatMinutes((record.totalBreakTime || 0) * 60),
            formatTime(record.netWorkTime),
            overtime,
            status,
            record.lateDuration || 0,
          ]);
        });

        csvLines.push([""]);
        csvLines.push(["----------------------------------------"]);
        csvLines.push([""]);
      });

      // CSV content oluştur
      const csvContent = csvLines
        .map((row) => row.map((field) => '"' + (field || "") + '"').join(","))
        .join("\n");

      // BOM ekle (Türkçe karakterler için)
      const BOM = "\uFEFF";
      const blob = new Blob([BOM + csvContent], {
        type: "text/csv;charset=utf-8;",
      });

      // Download link oluştur
      const link = document.createElement("a");
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute(
          "download",
          "detayli-puantaj-raporu-" +
          dateRange[0].format("DD-MM-YYYY") +
          "-" +
          dateRange[1].format("DD-MM-YYYY") +
          ".csv"
        );
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        message.success("Detaylı Excel dosyası başarıyla indirildi.");
      }
    } catch (error) {
      message.error("Excel export sırasında hata oluştu.");
    }
  }, [filteredTimesheetData, dateRange, formatTime]);

  // Özet PDF Export Fonksiyonu (Dropdown Kapalı - yeni sekmede önizleme)
  const exportSummaryToPDF = useCallback(() => {
    try {
      if (!filteredTimesheetData || filteredTimesheetData.length === 0) {
        message.warning("İndirilecek veri bulunamadı.");
        return;
      }

      const reportTitle = `Puantaj Raporu (Özet) - ${dateRange[0].format(
        "DD/MM/YYYY"
      )} - ${dateRange[1].format("DD/MM/YYYY")}`;

      // Özet istatistikler
      const totalUsers = filteredTimesheetData.length;
      const activeUsers = filteredTimesheetData.filter(
        (user) => user.netWorkingDays > 0
      ).length;
      const totalNetWorkHours = filteredTimesheetData.reduce(
        (sum, user) =>
          sum +
          user.dailyRecords.reduce(
            (userSum, record) => userSum + (record.netWorkTime || 0),
            0
          ),
        0
      );
      const lateUsers = filteredTimesheetData.filter(
        (user) => user.lateDays > 0
      ).length;

      // Özet kullanıcı tablosu
      const usersTableRows = filteredTimesheetData
        .map((user) => {
          const workingDays = user.dailyRecords.filter(
            (record) => record.entryTime && record.exitTime
          ).length;
          const attendanceRate =
            user.expectedWorkingDays > 0
              ? user.attendanceRate || (workingDays / user.expectedWorkingDays) * 100
              : 0;
          const totalLateMinutes = user.dailyRecords.reduce(
            (total, record) => total + (record.lateDuration || 0),
            0
          );
          const totalBreakMinutes = Math.round(
            (user.totalBreakHours || 0) * 60
          );
          const userNetWorkHours = user.dailyRecords.reduce(
            (total, record) => total + (record.netWorkTime || 0),
            0
          );

          return `
          <tr>
            <td>${user.userName}</td>
            <td>${user.position}</td>
            <td>${user.departmentName}</td>
            <td>${user.branchName}</td>
            <td>${workingDays} / ${user.expectedWorkingDays}</td>
            <td>${formatTime(userNetWorkHours)}</td>
            <td>${attendanceRate.toFixed(1)}%</td>
            <td>${totalLateMinutes} dk</td>
            <td>${totalBreakMinutes} dk</td>
          </tr>
        `;
        })
        .join("");

      const printContent = `
        <div class="report-header" style="text-align: center; margin-bottom: 40px; border-bottom: 2px solid #1890ff; padding-bottom: 20px;">
          <h1 style="color: #1890ff; margin-bottom: 10px;">${reportTitle}</h1>
          <div style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; margin-top: 30px;">
            <div style="text-align: center;">
              <div style="font-size: 24px; font-weight: bold; color: #0369a1;">${totalUsers}</div>
              <div style="font-size: 14px; color: #666;">Toplam Çalışan</div>
            </div>
            <div style="text-align: center;">
              <div style="font-size: 24px; font-weight: bold; color: #16a34a;">${activeUsers}</div>
              <div style="font-size: 14px; color: #666;">Aktif Çalışan</div>
            </div>
            <div style="text-align: center;">
              <div style="font-size: 24px; font-weight: bold; color: #ca8a04;">${formatTime(
        totalNetWorkHours
      )}</div>
              <div style="font-size: 14px; color: #666;">Net Çalışma</div>
            </div>
            <div style="text-align: center;">
              <div style="font-size: 24px; font-weight: bold; color: #dc2626;">${lateUsers}</div>
              <div style="font-size: 14px; color: #666;">Geç Kalan Çalışan</div>
            </div>
          </div>
        </div>
        
        <h2 style="color: #1890ff; margin-bottom: 20px;">Çalışan Özet Tablosu</h2>
        <table style="width: 100%; border-collapse: collapse; font-size: 12px;">
          <thead>
            <tr style="background-color: #f5f5f5;">
              <th style="border: 1px solid #ddd; padding: 10px; text-align: left;">Çalışan Adı</th>
              <th style="border: 1px solid #ddd; padding: 10px; text-align: left;">Pozisyon</th>
              <th style="border: 1px solid #ddd; padding: 10px; text-align: left;">Departman</th>
              <th style="border: 1px solid #ddd; padding: 10px; text-align: left;">Şube</th>
              <th style="border: 1px solid #ddd; padding: 10px; text-align: center;">Çalışma Günü</th>
              <th style="border: 1px solid #ddd; padding: 10px; text-align: center;">Net Çalışma</th>
              <th style="border: 1px solid #ddd; padding: 10px; text-align: center;">Devam Oranı</th>
              <th style="border: 1px solid #ddd; padding: 10px; text-align: center;">Geç Kalma</th>
              <th style="border: 1px solid #ddd; padding: 10px; text-align: center;">Toplam Mola</th>
            </tr>
          </thead>
          <tbody>
            ${usersTableRows}
          </tbody>
        </table>
      `;

      const printWindow = window.open("", "_blank");
      if (printWindow) {
        const htmlContent = `
          <html>
            <head>
              <title>Puantaj Raporu Özet - ${dateRange[0].format(
          "DD-MM-YYYY"
        )} - ${dateRange[1].format("DD-MM-YYYY")}</title>
              <style>
                body { font-family: Arial, sans-serif; margin: 20px; line-height: 1.4; }
                table { width: 100%; border-collapse: collapse; }
                th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
                th { background-color: #f5f5f5; font-weight: bold; }
                h1, h2 { margin-top: 0; }
              </style>
            </head>
            <body>
              ${printContent}
            </body>
          </html>
        `;

        printWindow.document.write(htmlContent);
        printWindow.document.close();
        // Yazdırma yapmadan yeni sekmede önizleme
        message.success("Özet PDF önizleme açıldı.");
      }
    } catch (error) {
      message.error("PDF export sırasında hata oluştu.");
    }
  }, [filteredTimesheetData, dateRange, formatTime]);

  // Özet PDF Export - İndirme/Yazdırma amaçlı (Ana İndir butonu)
  const exportSummaryToPDFDownload = useCallback(() => {
    try {
      if (!filteredTimesheetData || filteredTimesheetData.length === 0) {
        message.warning("İndirilecek veri bulunamadı.");
        return;
      }

      const printContent = (function () {
        const reportTitle = `Puantaj Raporu (Özet) - ${dateRange[0].format(
          "DD/MM/YYYY"
        )} - ${dateRange[1].format("DD/MM/YYYY")}`;
        const usersTableRows = filteredTimesheetData
          .map((user) => {
            const workingDays = user.dailyRecords.filter(
              (record) => record.entryTime && record.exitTime
            ).length;
            const attendanceRate =
              user.expectedWorkingDays > 0
                ? user.attendanceRate || (workingDays / user.expectedWorkingDays) * 100
                : 0;
            const totalLateMinutes = user.dailyRecords.reduce(
              (total, record) => total + (record.lateDuration || 0),
              0
            );
            const totalBreakMinutes = Math.round(
              (user.totalBreakHours || 0) * 60
            );
            const userNetWorkHours = user.dailyRecords.reduce(
              (total, record) => total + (record.netWorkTime || 0),
              0
            );
            return `
            <tr>
              <td>${user.userName}</td>
              <td>${user.position}</td>
              <td>${user.departmentName}</td>
              <td>${user.branchName}</td>
              <td>${workingDays} / ${user.expectedWorkingDays}</td>
              <td>${(Math.round(userNetWorkHours * 60) / 60).toFixed(2)} sa</td>
              <td>${attendanceRate.toFixed(1)}%</td>
              <td>${totalLateMinutes} dk</td>
              <td>${totalBreakMinutes} dk</td>
            </tr>`;
          })
          .join("");
        return `
          <div class="report-header" style="text-align:center;margin-bottom:24px">
            <h1 style="color:#1890ff;margin:0 0 8px">${reportTitle}</h1>
          </div>
          <table style="width:100%;border-collapse:collapse;font-size:12px">
            <thead>
              <tr style="background:#f5f5f5">
                <th style="border:1px solid #ddd;padding:8px;text-align:left">Çalışan</th>
                <th style="border:1px solid #ddd;padding:8px;text-align:left">Pozisyon</th>
                <th style="border:1px solid #ddd;padding:8px;text-align:left">Departman</th>
                <th style="border:1px solid #ddd;padding:8px;text-align:left">Şube</th>
                <th style="border:1px solid #ddd;padding:8px">Çalışma Günü</th>
                <th style="border:1px solid #ddd;padding:8px">Net Çalışma</th>
                <th style="border:1px solid #ddd;padding:8px">Devam Oranı</th>
                <th style="border:1px solid #ddd;padding:8px">Geç Kalma</th>
                <th style="border:1px solid #ddd;padding:8px">Toplam Mola</th>
              </tr>
            </thead>
            <tbody>${usersTableRows}</tbody>
          </table>`;
      })();

      const w = window.open("", "_blank");
      if (w) {
        const html = `
          <html>
            <head>
              <title>Puantaj Raporu (Özet) - ${dateRange[0].format(
          "DD-MM-YYYY"
        )} - ${dateRange[1].format("DD-MM-YYYY")}</title>
              <style>
                body{font-family:Arial,sans-serif;margin:20px;}
                table,th,td{border:1px solid #ddd}
              </style>
            </head>
            <body>${printContent}</body>
          </html>`;
        w.document.write(html);
        w.document.close();
        w.print();
        w.close();
      }
    } catch (error) {
      message.error("PDF export sırasında hata oluştu.");
    }
  }, [filteredTimesheetData, dateRange]);

  // Özet Excel Export Fonksiyonu (Dropdown Kapalı)
  const exportSummaryToExcel = useCallback(() => {
    try {
      if (!filteredTimesheetData || filteredTimesheetData.length === 0) {
        message.warning("İndirilecek veri bulunamadı.");
        return;
      }

      const reportTitle =
        "PUANTAJ RAPORU (ÖZET) - " +
        dateRange[0].format("DD/MM/YYYY") +
        " - " +
        dateRange[1].format("DD/MM/YYYY");

      // Genel istatistikler
      const totalUsers = filteredTimesheetData.length;
      const activeUsers = filteredTimesheetData.filter(
        (user) => user.netWorkingDays > 0
      ).length;
      const totalNetWorkHours = filteredTimesheetData.reduce(
        (sum, user) =>
          sum +
          user.dailyRecords.reduce(
            (userSum, record) => userSum + (record.netWorkTime || 0),
            0
          ),
        0
      );
      const lateUsers = filteredTimesheetData.filter(
        (user) => user.lateDays > 0
      ).length;

      const csvLines = [];

      // Başlık ve genel bilgiler
      csvLines.push([reportTitle]);
      csvLines.push([""]);
      csvLines.push(["GENEL İSTATİSTİKLER"]);
      csvLines.push(["Toplam Çalışan", totalUsers]);
      csvLines.push(["Aktif Çalışan", activeUsers]);
      csvLines.push(["Net Çalışma", formatTime(totalNetWorkHours)]);
      csvLines.push(["Geç Kalan Çalışan", lateUsers]);
      csvLines.push([""]);

      // Özet tablo
      csvLines.push(["ÇALIŞAN ÖZET TABLOSU"]);
      csvLines.push([
        "Çalışan Adı",
        "E-mail",
        "Pozisyon",
        "Departman",
        "Şube",
        "Çalışma Günü",
        "Beklenen Gün",
        "Net Çalışma",
        "Devam Oranı (%)",
        "Geç Kalma (dk)",
        "Toplam Mola (dk)",
        "Ortalama Günlük Saat",
      ]);

      filteredTimesheetData.forEach((user) => {
        const workingDays = user.dailyRecords.filter(
          (record) => record.entryTime && record.exitTime
        ).length;
        const attendanceRate =
          user.expectedWorkingDays > 0 ? user.attendanceRate || (workingDays / user.expectedWorkingDays) * 100 : 0;
        const totalLateMinutes = user.dailyRecords.reduce(
          (total, record) => total + (record.lateDuration || 0),
          0
        );
        const totalBreakMinutes = Math.round((user.totalBreakHours || 0) * 60);
        const userNetWorkHours = user.dailyRecords.reduce(
          (total, record) => total + (record.netWorkTime || 0),
          0
        );

        csvLines.push([
          user.userName,
          user.userEmail,
          user.position,
          user.departmentName,
          user.branchName,
          workingDays,
          user.expectedWorkingDays,
          formatTime(userNetWorkHours),
          attendanceRate.toFixed(1),
          totalLateMinutes,
          totalBreakMinutes,
          (user.averageDailyHours
            ? user.averageDailyHours.toFixed(2)
            : "0.00") + " saat",
        ]);
      });

      const csvContent = csvLines
        .map((row) => row.map((field) => '"' + (field || "") + '"').join(","))
        .join("\n");

      const BOM = "\uFEFF";
      const blob = new Blob([BOM + csvContent], {
        type: "text/csv;charset=utf-8;",
      });

      const link = document.createElement("a");
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute(
          "download",
          "ozet-puantaj-raporu-" +
          dateRange[0].format("DD-MM-YYYY") +
          "-" +
          dateRange[1].format("DD-MM-YYYY") +
          ".csv"
        );
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        message.success("Özet Excel dosyası başarıyla indirildi.");
      }
    } catch (error) {
      message.error("Excel export sırasında hata oluştu.");
    }
  }, [filteredTimesheetData, dateRange, formatTime]);

  // ========== ESKİ FONKSİYONLAR (Silindi - Artık Kullanılmıyor) ==========

  // Print içeriği oluştur
  const generateUserPrintContent = useCallback(
    (user: TimesheetUserData) => {
      const workingDays = user.dailyRecords.filter(
        (record) => record.entryTime && record.exitTime
      ).length;
      const attendanceRate =
        user.expectedWorkingDays > 0 ? user.attendanceRate || (workingDays / user.expectedWorkingDays) * 100 : 0;
      const totalLateMinutes = user.dailyRecords.reduce(
        (total, record) => total + (record.lateDuration || 0),
        0
      );
      const totalBreakMinutes = Math.round((user.totalBreakHours || 0) * 60);

      const dailyRecordsRows = user.dailyRecords
        .map(
          (record) => `
      <tr>
        <td>${dayjs(record.date).format("DD.MM.YYYY")}</td>
        <td>${record.entryTime || "-"}</td>
        <td>${record.exitTime || "-"}</td>
        <td>${formatTime(record.totalWorkHours)}</td>
        <td>${formatMinutes((record.totalBreakTime || 0) * 60)} dk</td>
        <td>${formatTime(record.netWorkTime)}</td>
        <td>${record.overtime?.duration ? formatTime(record.overtime.duration) : "-"
            }</td>
        <td>${!record.entryTime && !record.exitTime
              ? "İzinli"
              : record.isLate
                ? "Geç (+" + record.lateDuration + " dk)"
                : "Zamanında"
            }</td>
      </tr>
    `
        )
        .join("");

      return `
      <div class="header">
        <h1>${user.userName} - Puantaj Raporu</h1>
        <p>${dateRange[0].format("DD/MM/YYYY")} - ${dateRange[1].format(
        "DD/MM/YYYY"
      )}</p>
      </div>
      
      <div class="user-info">
        <h3>Kullanıcı Bilgileri</h3>
        <p><strong>Pozisyon:</strong> ${user.position}</p>
        <p><strong>Departman:</strong> ${user.departmentName}</p>
        <p><strong>Şube:</strong> ${user.branchName}</p>
      </div>

      <div class="stats-grid">
        <div class="stat-card">
          <div class="stat-title">Çalışma Günü</div>
          <div class="stat-value">${workingDays} / ${user.expectedWorkingDays
        }</div>
        </div>
        <div class="stat-card">
          <div class="stat-title">Toplam Çalışma</div>
          <div class="stat-value">${formatTime(user.totalWorkingHours)}</div>
        </div>
        <div class="stat-card">
          <div class="stat-title">Devam Oranı</div>
          <div class="stat-value">${attendanceRate.toFixed(1)}%</div>
        </div>
        <div class="stat-card">
          <div class="stat-title">Geç Kalma</div>
          <div class="stat-value">${totalLateMinutes} dk</div>
        </div>
        <div class="stat-card">
          <div class="stat-title">Toplam Mola</div>
          <div class="stat-value">${totalBreakMinutes} dk</div>
        </div>
        <div class="stat-card">
          <div class="stat-title">Ortalama Günlük</div>
          <div class="stat-value">${user.averageDailyHours ? user.averageDailyHours.toFixed(2) : "0.00"
        } sa</div>
        </div>
      </div>

      <table>
        <thead>
          <tr>
            <th>Tarih</th>
            <th>Giriş</th>
            <th>Çıkış</th>
            <th>Net Çalışma</th>
            <th>Mola</th>
            <th>Fazla/Eksik Mesai</th>
            <th>Durum</th>
          </tr>
        </thead>
        <tbody>
          ${dailyRecordsRows}
        </tbody>
      </table>
    `;
    },
    [dateRange, formatTime]
  );

  // Kullanıcıya özel PDF Export
  const exportUserToPDF = useCallback(
    (user: TimesheetUserData) => {
      try {
        // Print için geçici olarak sadece bu kullanıcıyı göster
        const printContent = generateUserPrintContent(user);

        // Yeni pencere aç ve yazdır
        const printWindow = window.open("", "_blank");
        if (printWindow) {
          printWindow.document.write(`
          <html>
            <head>
              <title>${user.userName} - Puantaj Raporu</title>
              <style>
                body { font-family: Arial, sans-serif; margin: 20px; }
                .header { text-align: center; margin-bottom: 30px; }
                .user-info { margin-bottom: 20px; }
                .stats-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px; margin-bottom: 30px; }
                .stat-card { border: 1px solid #ddd; padding: 15px; text-align: center; border-radius: 5px; }
                .stat-title { font-size: 12px; color: #666; margin-bottom: 5px; }
                .stat-value { font-size: 18px; font-weight: bold; }
                table { width: 100%; border-collapse: collapse; margin-top: 20px; }
                th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
                th { background-color: #f2f2f2; font-weight: bold; }
                .text-center { text-align: center; }
              </style>
            </head>
            <body>
              ${printContent}
            </body>
          </html>
        `);
          printWindow.document.close();
          printWindow.print();
          printWindow.close();
          message.success(user.userName + " için PDF export başlatıldı.");
        }
      } catch (error) {
        message.error("PDF export sırasında hata oluştu.");
      }
    },
    [generateUserPrintContent]
  );

  // Kullanıcıya özel Excel Export
  const exportUserToExcel = useCallback(
    (user: TimesheetUserData) => {
      try {
        const workingDays = user.dailyRecords.filter(
          (record) => record.entryTime && record.exitTime
        ).length;
        const attendanceRate =
          user.expectedWorkingDays > 0 ? user.attendanceRate || (workingDays / user.expectedWorkingDays) * 100 : 0;
        const totalLateMinutes = user.dailyRecords.reduce(
          (total, record) => total + (record.lateDuration || 0),
          0
        );
        const totalBreakMinutes = Math.round((user.totalBreakHours || 0) * 60);
        const userNetWorkHours = user.dailyRecords.reduce(
          (total, record) => total + (record.netWorkTime || 0),
          0
        );

        // Kullanıcı bilgileri
        const userInfo = [
          ["Kullanıcı Adı", user.userName],
          ["Pozisyon", user.position],
          ["Departman", user.departmentName],
          ["Şube", user.branchName],
          ["Çalışma Günü", workingDays + " / " + user.expectedWorkingDays],
          ["Net Çalışma", formatTime(userNetWorkHours)],
          ["Devam Oranı", attendanceRate.toFixed(1) + "%"],
          ["Geç Kalma", totalLateMinutes + " dk"],
          ["Toplam Mola", totalBreakMinutes + " dk"],
          [""], // Boş satır
        ];

        // Günlük kayıtlar headers
        const dailyHeaders = [
          "Tarih",
          "Giriş Saati",
          "Çıkış Saati",
          "Net Çalışma",
          "Mola (dk)",
          "Fazla/Eksik Mesai",
          "Durum",
        ];

        // Günlük kayıtlar data
        const dailyData = user.dailyRecords.map((record) => [
          dayjs(record.date).format("DD.MM.YYYY"),
          record.entryTime || "-",
          record.exitTime || "-",
          formatTime(record.totalWorkHours),
          formatMinutes((record.totalBreakTime || 0) * 60),
          formatTime(record.netWorkTime),
          record.overtime?.duration
            ? formatTime(record.overtime.duration)
            : "-",
          !record.entryTime && !record.exitTime
            ? "İzinli"
            : record.isLate
              ? "Geç (+" + record.lateDuration + " dk)"
              : "Zamanında",
        ]);

        // CSV content oluştur
        const csvContent = [
          ["KULLANICI BİLGİLERİ"],
          ...userInfo,
          ["GÜNLÜK KAYITLAR"],
          dailyHeaders,
          ...dailyData,
        ]
          .map((row) => row.map((field) => '"' + field + '"').join(","))
          .join("\n");

        // BOM ekle
        const BOM = "\uFEFF";
        const blob = new Blob([BOM + csvContent], {
          type: "text/csv;charset=utf-8;",
        });

        // Download
        const link = document.createElement("a");
        if (link.download !== undefined) {
          const url = URL.createObjectURL(blob);
          link.setAttribute("href", url);
          link.setAttribute(
            "download",
            user.userName +
            "-puantaj-" +
            dateRange[0].format("DD-MM-YYYY") +
            "-" +
            dateRange[1].format("DD-MM-YYYY") +
            ".csv"
          );
          link.style.visibility = "hidden";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          message.success(user.userName + " için Excel dosyası indirildi.");
        }
      } catch (error) {
        message.error("Excel export sırasında hata oluştu.");
      }
    },
    [dateRange, formatTime]
  );

  // ========== ESKİ FONKSİYONLAR (Silindi - Artık Kullanılmıyor) ==========

  // Verileri getiren fonksiyon
  const fetchTimesheetData = useCallback(
    async (startDate: string, endDate: string) => {
      setLoading(true);
      try {
        const response = await reportService.getTimesheetReport(
          startDate,
          endDate
        );

        // Debug için response'u console'a yazdır

        // API service extractData fonksiyonu zaten response.data.data döndürüyor
        // Bu yüzden response direkt TimesheetUserData[] array'i olmalı
        if (response && Array.isArray(response)) {
          setTimesheetData(response);
        } else {
          setTimesheetData([]);
        }
      } catch (error: any) {
        handleApiError(error, t);
        setTimesheetData([]);
      } finally {
        setLoading(false);
      }
    },
    [t]
  );

  // Sayfa yüklendiğinde verileri getir
  useEffect(() => {
    const startDate = dateRange[0].format("YYYY-MM-DD");

    // Kullanıcı 31 Ağustos seçtiyse API'ye 1 Eylül gönder
    const apiEndDate = dateRange[1]
      .add(1, "day")
      .format("YYYY-MM-DD");

    console.log({
      selectedStartDate: dateRange[0].format("YYYY-MM-DD"),
      selectedEndDate: dateRange[1].format("YYYY-MM-DD"),
      apiStartDate: startDate,
      apiEndDate,
    });

    fetchTimesheetData(startDate, apiEndDate);
    fetchPayrollData();
  }, [dateRange, fetchTimesheetData, fetchPayrollData]);

  // Tarih aralığı değiştiğinde
  const handleDateRangeChange = (dates: any) => {
    if (dates) {
      setDateRange([dates[0], dates[1]]);
    }
  };

  // Status renk haritası - "Erken Çıktı" statusü kullanılmıyor
  const statusColorMap: Record<string, string> = {
    "Geç Geldi": "red",
    "Zamanında Geldi": "green",
    "Zamanında Çıktı": "green",
    "Eksik Mesai": "red",
    "Fazla Mesai": "orange",
    "Mola Aşımı": "purple",
  };

  const getStatusColorLabels = (status: string): string => {
    const matchedKey = Object.keys(statusColorMap).find((key) =>
      status.includes(key)
    );

    return matchedKey ? statusColorMap[matchedKey] : "default";
  };

  // Kullanıcı detay tablosu için kolonlar
  const getUserDetailColumns = () => [
    {
      title: "Tarih",
      dataIndex: "date",
      key: "date",
      render: (date: string) => (
        <div>
          <div>{dayjs(date).format("DD.MM.YYYY")}</div>
          <small className="text-muted">{dayjs(date).format("dddd")}</small>
        </div>
      ),
    },
    {
      title: "Giriş",
      dataIndex: "entryTime",
      key: "entryTime",
      render: (time: string | null) => (
        <span className={time ? "fw-bold text-success" : "text-muted"}>
          {time || "-"}
        </span>
      ),
    },
    {
      title: "Çıkış",
      dataIndex: "exitTime",
      key: "exitTime",
      render: (time: string | null) => (
        <span className={time ? "fw-bold text" : "text-muted"}>
          {time || "-"}
        </span>
      ),
    },
    {
      title: "Net Çalışma",
      dataIndex: "netWorkTime",
      key: "netWorkTime",
      render: (hours: number, record: DailyRecord) => {
        if (
          record.leaveStatus !== "hourly (approved)" &&
          record.leaveStatus !== null
        ) {
          return <span className="text-muted">-</span>;
        } else {
          return <span className="fw-bold text-info">{formatTime(hours)}</span>;
        }
      },
    },
    {
      title: "Mola",
      dataIndex: "totalBreakTime",
      key: "totalBreakTime",
      render: (hours: number, record: DailyRecord) => {
        if (
          record.leaveStatus !== "hourly (approved)" &&
          record.leaveStatus !== null
        ) {
          return <span className="text-muted">-</span>;
        } else {
          return (
            <div>
              <div className="fw">{formatTime(hours) || "-"}</div>
            </div>
          );
        }
      },
    },
    {
      title: "Fazla/Eksik Mesai",
      key: "overtime",
      render: (record: DailyRecord) => {
        if (
          record.leaveStatus !== "hourly (approved)" &&
          record.leaveStatus !== null
        ) {
          return <span className="text-muted">-</span>;
        } else {
          const overtimeDuration = record.overtimeMinutes || 0;
          const onayliMesaiMinutes = record.onayliMesai?.durationMinutes;
          const mainText = formatHoursAndMinutes(overtimeDuration) || "-";
          const onayliText =
            onayliMesaiMinutes != null && onayliMesaiMinutes > 0
              ? ` (${formatHoursAndMinutes(onayliMesaiMinutes)} onaylı)`
              : "";

          if (overtimeDuration > 0) {
            return (
              <span className="fw-bold text-warning">
                {mainText}
                {onayliText}
              </span>
            );
          } else {
            return (
              <span className="fw text-danger">
                {mainText}
                {onayliText}
              </span>
            );
          }
        }
      },
    },
    {
      title: "Saha",
      key: "fieldSessions",
      render: (record: DailyRecord) => {
        const sessions = record.fieldSessions || [];
        if (!sessions.length) return <span className="text-muted">-</span>;
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            {sessions.map((session, index) => (
              <div key={index}>
                <Tag color={session.isOngoing ? "processing" : "blue"}>
                  {session.startTime || "?"} -{" "}
                  {session.endTime || (session.isOngoing ? "Sahada" : "?")}
                </Tag>
                {session.activityTitle && (
                  <div>
                    <small className="text-muted">{session.activityTitle}</small>
                  </div>
                )}
              </div>
            ))}
          </div>
        );
      },
    },
    {
      title: "Durum",
      key: "status",
      render: (record: DailyRecord) => {
        if (record.leaveStatus) {
          const leaveType = record.leaveDetail?.type || "Bilinmeyen";

          if (record.leaveDetail?.isHourly) {
            return (
              <div
                style={{ display: "flex", flexDirection: "column", gap: "4px" }}
              >
                <Tag color="orange">İzinli: {leaveTypesLabels[leaveType]}</Tag>
                <Tag color="orange">
                  {record.leaveDetail?.startTime}-{record.leaveDetail?.endTime}{" "}
                  | {record.leaveDetail?.durationMinutes}dk
                </Tag>
              </div>
            );
          } else {
            return (
              <div
                style={{ display: "flex", flexDirection: "column", gap: "4px" }}
              >
                <Tag color="orange">İzinli: {leaveTypesLabels[leaveType]}</Tag>
              </div>
            );
          }
        } else {
          const statusTags = (record.status || []).filter(
            (status) => status !== "Erken Çıktı" // "Erken Çıktı" statusünü filtrele
          );
          // Etiket yoksa boş bırakma: sahadaysa "Sahada", giriş/çıkış da yoksa "Kayıt Yok"
          if (!statusTags.length) {
            if (record.fieldSessions?.length) return <Tag color="blue">Sahada</Tag>;
            if (!record.entryTime && !record.exitTime) return <Tag>Kayıt Yok</Tag>;
          }
          return (
            <div
              style={{ display: "flex", flexDirection: "column", gap: "4px" }}
            >
              {statusTags.map((status, index) => (
                <Tag key={index} color={getStatusColorLabels(status)}>
                  {status}
                </Tag>
              ))}
            </div>
          );
        }
      },
    },
  ];

  // Bordro hesaplama detayları render fonksiyonu
  const renderCalculationBreakdown = (breakdown: CompanyPayrollItem['calculationBreakdown']) => (
    <Row gutter={[24, 24]}>
      <Col xs={24} md={12}>
        <Card title={<><PlusCircleOutlined style={{ color: '#52c41a' }} /> Eklemeler</>} bordered={false} size="small">
          <List itemLayout="horizontal">
            <List.Item>
              <List.Item.Meta
                title="Fazla Mesai Kazancı"
                description={`${breakdown.totalOvertimeHours.toFixed(2)} saat`}
              />
              <Text strong style={{ color: '#389e0d' }}>{`+ ${formatCurrency(breakdown.overtimeAddition)}`}</Text>
            </List.Item>
            <List.Item>
              <List.Item.Meta
                title="Resmi Tatil Çalışması Kazancı"
                description={`${breakdown.totalHolidayWorkHours.toFixed(2)} saat`}
              />
              <Text strong style={{ color: '#389e0d' }}>{`+ ${formatCurrency(breakdown.holidayWorkAddition)}`}</Text>
            </List.Item>
          </List>
        </Card>
      </Col>
      <Col xs={24} md={12}>
        <Card title={<><MinusCircleOutlined style={{ color: '#f5222d' }} /> Kesintiler</>} bordered={false} size="small">
          <List itemLayout="horizontal">
            <List.Item>
              <List.Item.Meta
                title="Ücretsiz İzin Kesintisi"
                description={`${breakdown.unpaidLeaveDays} gün`}
              />
              <Text strong style={{ color: '#cf1322' }}>{`- ${formatCurrency(breakdown.unpaidLeaveDeduction)}`}</Text>
            </List.Item>
          </List>
        </Card>
      </Col>
      <Col xs={24}>
        <Card title="Hesaplama Detayları" bordered={false} size="small">
          <Row>
            <Col span={12}>
              <Statistic title={<><DollarOutlined /> Günlük Net Ücret</>} value={formatCurrency(breakdown.dailyNetWage)} precision={2} />
            </Col>
            <Col span={12}>
              <Statistic title={<><ClockCircleOutlined /> Saatlik Net Ücret</>} value={formatCurrency(breakdown.hourlyNetWage)} precision={2} />
            </Col>
          </Row>
        </Card>
      </Col>
    </Row>
  );

  // Bordro kullanıcı paneli render fonksiyonu
  const renderPayrollUserPanel = (u: CompanyPayrollItem) => {
    return (
      <Panel
        header={
          <div className="user-card-header" style={{ width: '100%' }}>
            <Row align="middle" justify="space-between" style={{ width: '100%' }}>
              <Col xs={24} sm={8} md={7}>
                <div className="d-flex align-items-center">
                  <Avatar
                    size={60}
                    src={u.profilePicture ? getFileUrl(u.profilePicture) : u.gender === 'male' ? '/assets/img/man.png' : '/assets/img/woman.png'}
                    icon={<UserOutlined />}
                    className="me-3"
                  />
                  <div>
                    <h4 className="mb-1 text-primary fw-bold">{`${u.firstName} ${u.lastName}`}</h4>
                    <Text type="secondary">Departman: {u.department}</Text>
                  </div>
                </div>
              </Col>
              <Col xs={24} sm={16} md={17}>
                <Row gutter={[8, 8]} justify="end" align="middle">
                  <Col>
                    <Statistic title="Temel Net Maaş" value={formatCurrency(u.baseNetSalary)} />
                  </Col>
                  <Col>
                    <Statistic title="Toplam Eklemeler" value={formatCurrency(u.totalAdditions)} valueStyle={{ color: '#52c41a' }} />
                  </Col>
                  <Col>
                    <Statistic title="Toplam Kesintiler" value={formatCurrency(u.totalDeductions)} valueStyle={{ color: '#f5222d' }} />
                  </Col>
                  <Col>
                    <Statistic title="Ödenecek Tutar" value={formatCurrency(u.finalPayableAmount)} valueStyle={{ fontWeight: 'bold' }} />
                  </Col>
                  <Col>
                    <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                      <Button
                        type="default"
                        size="small"
                        icon={<FilePdfOutlined />}
                        onClick={() => exportUserPayrollToPDF(u)}
                        style={{
                          fontSize: "12px",
                          padding: "4px 8px",
                          height: "28px",
                        }}
                      >
                        PDF İndir
                      </Button>
                      <Button
                        type="default"
                        size="small"
                        icon={<FileExcelOutlined />}
                        onClick={() => exportUserPayrollToExcel(u)}
                        style={{
                          fontSize: "12px",
                          padding: "4px 8px",
                          height: "28px",
                        }}
                      >
                        Excel İndir
                      </Button>
                    </div>
                  </Col>
                </Row>
              </Col>
            </Row>
          </div>
        }
        key={u.userId}
        style={{ marginBottom: 16, border: '1px solid #d9d9d9', borderRadius: 8 }}
      >
        {renderCalculationBreakdown(u.calculationBreakdown)}
      </Panel>
    );
  };

  // Kullanıcı card bileşeni
  const renderUserCard = (user: TimesheetUserData) => {
    const workingDays = user.dailyRecords.filter(
      (record) => record.entryTime && record.exitTime
    ).length;
    const attendanceRate =
      user.expectedWorkingDays > 0 ? user.attendanceRate || (workingDays / user.expectedWorkingDays) * 100 : 0;

    // Toplam geç kalma dakikalarını hesapla
    const totalLateMinutes = user.dailyRecords.reduce((total, record) => {
      return total + (record.lateDuration || 0);
    }, 0);

    // Toplam mola dakikalarını hesapla (saat -> dakika)
    const totalBreakMinutes = Math.round((user.totalBreakHours || 0) * 60);

    // Toplam net çalışma süresini hesapla (günlük kayıtlardan)
    const totalNetWorkHours = user.dailyRecords.reduce((total, record) => {
      return total + (record.netWorkTime || 0);
    }, 0);

    return (
      <Panel
        header={
          <div className="user-card-header" style={{ width: "100%" }}>
            <Row
              align="middle"
              justify="space-between"
              style={{ width: "100%" }}
            >
              <Col span={7}>
                <div className="d-flex align-items-center">
                  <Avatar
                    size={60}
                    src={user.profilePicture ? getFileUrl(user.profilePicture) : user.gender === 'male' ? '/assets/img/man.png' : '/assets/img/woman.png'}
                    icon={<UserOutlined />}
                    className="me-3"
                    style={{
                      backgroundColor: "#1890ff",
                      fontSize: "24px",
                    }}
                  />
                  <div>
                    <h4 className="mb-1 text-primary fw-bold">
                      {user.userName}
                    </h4>
                    <div className="text-muted">
                      <div className="mb-1">
                        <span className="me-3">
                          <strong>Pozisyon:</strong> {user.position}
                        </span>
                      </div>
                      <div className="mb-1">
                        <span className="me-3">
                          <strong>Departman:</strong> {user.departmentName}
                        </span>
                      </div>
                      <div className="mb-1">
                        <span className="me-3">
                          <strong>Şube:</strong> {user.branchName}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </Col>

              <Col span={17}>
                {/* İlk satır - 3 kart */}
                <Row gutter={[16, 8]} justify="end" style={{ marginBottom: 8 }}>
                  <Col>
                    <Card
                      size="small"
                      style={{
                        textAlign: "center",
                        backgroundColor: "#f0f9ff",
                        border: "1px solid #bae6fd",
                      }}
                    >
                      <Statistic
                        title="Çalışma Günü"
                        value={workingDays}
                        suffix={`/ ${user.expectedWorkingDays}`}
                        valueStyle={{
                          color: "#0369a1",
                          fontSize: 18,
                          fontWeight: "bold",
                        }}
                      />
                    </Card>
                  </Col>

                  <Col>
                    <Card
                      size="small"
                      style={{
                        textAlign: "center",
                        backgroundColor: "#f0fdf4",
                        border: "1px solid #bbf7d0",
                      }}
                    >
                      <Statistic
                        title="Net Çalışma"
                        value={formatTime(totalNetWorkHours)}
                        valueStyle={{
                          color: "#16a34a",
                          fontSize: 18,
                          fontWeight: "bold",
                        }}
                      />
                    </Card>
                  </Col>

                  <Col>
                    <Card
                      size="small"
                      style={{
                        textAlign: "center",
                        backgroundColor: "#fefce8",
                        border: "1px solid #fde047",
                      }}
                    >
                      <Statistic
                        title="Devam Oranı"
                        value={attendanceRate.toFixed(1)}
                        suffix="%"
                        valueStyle={{
                          color: "#ca8a04",
                          fontSize: 18,
                          fontWeight: "bold",
                        }}
                      />
                    </Card>
                  </Col>
                </Row>

                {/* İkinci satır - 3 kart */}
                <Row gutter={[16, 8]} justify="end">
                  <Col>
                    <Card
                      size="small"
                      style={{
                        textAlign: "center",
                        backgroundColor:
                          totalLateMinutes > 0 ? "#fef2f2" : "#f0fdf4",
                        border:
                          totalLateMinutes > 0
                            ? "1px solid #fca5a5"
                            : "1px solid #bbf7d0",
                      }}
                    >
                      <Statistic
                        title="Geç Kalma"
                        value={totalLateMinutes}
                        suffix="dk"
                        valueStyle={{
                          color: totalLateMinutes > 0 ? "#dc2626" : "#16a34a",
                          fontSize: 18,
                          fontWeight: "bold",
                        }}
                      />
                    </Card>
                  </Col>

                  <Col>
                    <Card
                      size="small"
                      style={{
                        textAlign: "center",
                        backgroundColor: "#f3f4f6",
                        border: "1px solid #d1d5db",
                      }}
                    >
                      <Statistic
                        title="Toplam Mola"
                        value={totalBreakMinutes}
                        suffix="dk"
                        valueStyle={{
                          color: "#6b7280",
                          fontSize: 18,
                          fontWeight: "bold",
                        }}
                      />
                    </Card>
                  </Col>

                  <Col>
                    <Card
                      size="small"
                      style={{
                        textAlign: "center",
                        backgroundColor: "#fef3c7",
                        border: "1px solid #fbbf24",
                      }}
                    >
                      <Statistic
                        title="İzin Günleri"
                        value={(user.paidLeaveDays || 0) + (user.unpaidLeaveDays || 0)}
                        suffix="gün"
                        valueStyle={{
                          color: "#d97706",
                          fontSize: 18,
                          fontWeight: "bold",
                        }}
                      />
                    </Card>
                  </Col>
                </Row>
              </Col>

              <Col
                span={0}
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <Button
                  type="default"
                  size="small"
                  icon={<FilePdfOutlined />}
                  onClick={() => exportUserTimesheetToPDF(user)}
                  style={{
                    fontSize: "12px",
                    padding: "4px 8px",
                    height: "28px",
                  }}
                >
                  PDF İndir
                </Button>
                <Button
                  type="default"
                  size="small"
                  icon={<FileExcelOutlined />}
                  onClick={() => exportUserTimesheetToExcel(user)}
                  style={{
                    fontSize: "12px",
                    padding: "4px 8px",
                    height: "28px",
                  }}
                >
                  Excel İndir
                </Button>
              </Col>
            </Row>
          </div>
        }
        key={user.userId}
        style={{
          marginBottom: "16px",
          border: "1px solid #d9d9d9",
          borderRadius: "8px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        <div style={{ padding: "16px 0" }}>
          <Title level={5} className="mb-3" style={{ color: "#1890ff" }}>
            {user.userName} - Günlük Çalışma Detayları
          </Title>

          <Table
            columns={getUserDetailColumns()}
            dataSource={user.dailyRecords}
            rowKey="date"
            onRow={(record) => ({
              style: {
                backgroundColor: record.leaveStatus ? "#fff7e6" : "transparent",
              },
            })}
            pagination={false}
            size="small"
            className="user-detail-table"
            style={{ backgroundColor: "#fafafa" }}
            bordered
          />
        </div>
      </Panel>
    );
  };

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        {/* Breadcrumb */}
        <div className="page-header">
          <div className="row align-items-center">
            <div className="col">
              <h3 className="page-title">
                <BarChartOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                Puantaj & Maaş Raporları
              </h3>
              <ul className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to={all_routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">Raporlar</li>
                <li className="breadcrumb-item active">Puantaj & Maaş</li>
              </ul>
            </div>
            <div className="col-auto ms-auto">
              <div className="d-flex align-items-center gap-2">
                <Segmented
                  options={[
                    { label: 'Aktif', value: 'active' },
                    { label: 'Pasif', value: 'passive' },
                    { label: 'İşten Ayrılmış', value: 'resigned' },
                    { label: 'Tümü', value: 'all' },
                  ]}
                  value={statusFilter}
                  onChange={(value) => setStatusFilter(value as 'active' | 'passive' | 'resigned' | 'all')}
                />
                <RangePicker
                  value={dateRange}
                  onChange={handleDateRangeChange}
                  format="DD/MM/YYYY"
                  placeholder={["Başlangıç Tarihi", "Bitiş Tarihi"]}
                />
                {/* Puantaj raporu için export butonları */}
                {activeTab === 'timesheet' && (
                  <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                    <Button
                      type="default"
                      icon={<FilePdfOutlined />}
                      onClick={exportAllTimesheetToPDF}
                      disabled={!filteredTimesheetData || filteredTimesheetData.length === 0}
                      style={{
                        backgroundColor: "white",
                        borderColor: "#d9d9d9",
                        color: "#000"
                      }}
                    >
                      Tüm Çalışanlar PDF İndir
                    </Button>
                    <Button
                      type="default"
                      icon={<FileExcelOutlined />}
                      onClick={exportAllTimesheetToExcel}
                      disabled={!filteredTimesheetData || filteredTimesheetData.length === 0}
                      style={{
                        backgroundColor: "white",
                        borderColor: "#d9d9d9",
                        color: "#000"
                      }}
                    >
                      Tüm Çalışanlar Excel İndir
                    </Button>
                  </div>
                )}
                {/* Maaş raporu için export butonları */}
                {activeTab === 'payroll' && (
                  <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                    <Button
                      type="default"
                      icon={<FilePdfOutlined />}
                      onClick={exportAllPayrollToPDF}
                      disabled={!filteredPayrollData || filteredPayrollData.length === 0}
                      style={{
                        backgroundColor: "white",
                        borderColor: "#d9d9d9",
                        color: "#000"
                      }}
                    >
                      Tüm Çalışanlar PDF İndir
                    </Button>
                    <Button
                      type="default"
                      icon={<FileExcelOutlined />}
                      onClick={exportAllPayrollToExcel}
                      disabled={!filteredPayrollData || filteredPayrollData.length === 0}
                      style={{
                        backgroundColor: "white",
                        borderColor: "#d9d9d9",
                        color: "#000"
                      }}
                    >
                      Tüm Çalışanlar Excel İndir
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Ana İçerik */}
        <div className="row">
          <div className="col-md-12">
            <Card
              bordered={false}
              style={{ boxShadow: "0 4px 8px rgba(0,0,0,0.1)" }}
            >
              <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                size="large"
                type="card"
                style={{ marginTop: 0 }}
                tabBarStyle={{
                  marginBottom: 0,
                  background: 'linear-gradient(135deg, rgba(248, 250, 252, 0.95) 0%, rgba(226, 232, 240, 0.95) 100%)',
                  borderRadius: '12px 12px 0 0',
                  padding: '8px 16px 0',
                  position: 'sticky',
                  top: '50px',
                  zIndex: 100,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  backdropFilter: 'blur(12px)',
                  borderBottom: '1px solid rgba(226, 232, 240, 0.8)',
                  transition: 'all 0.3s ease'
                }}
                items={[
                  {
                    key: 'timesheet',
                    label: (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontSize: '16px',
                        fontWeight: '600',
                        padding: '4px 8px'
                      }}>
                        <BarChartOutlined style={{ fontSize: '18px', color: '#1890ff' }} />
                        <span>Puantaj</span>
                      </div>
                    ),
                    children: (
                      <div style={{
                        height: 'calc(100vh - 200px)',
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        padding: '0 20px 20px 20px'
                      }}>
                        {/* <div style={{ 
                          background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)', 
                          borderRadius: '12px', 
                          padding: '16px',
                          marginBottom: '16px',
                          position: 'sticky',
                          top: '0px',
                          zIndex: 10,
                          backdropFilter: 'blur(8px)',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                        }}>
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between',
                            marginBottom: '12px'
                          }}>
                            <Title level={4} style={{ margin: 0, color: '#1890ff' }}>
                              📋 Puantaj Detayları
                            </Title>
                            <span className="text-muted" style={{ fontSize: '12px' }}>
                              {dateRange[0].format("DD/MM/YYYY")} - {dateRange[1].format("DD/MM/YYYY")}
                            </span>
                          </div>
                        </div> */}

                        <div style={{ paddingTop: '10px' }}>
                          {loading ? (
                            <LoadingSpinner centered />
                          ) : (
                            filteredTimesheetData.length > 0 ? (
                              <Collapse
                                className="user-cards"
                                expandIcon={({ isActive }) => (
                                  <DownOutlined rotate={isActive ? 180 : 0} />
                                )}
                                expandIconPosition="end"
                                size="large"
                                ghost={false}
                                accordion
                                activeKey={selectedUserId ? [selectedUserId.toString()] : []}
                                onChange={(keys) => {
                                  const key = Array.isArray(keys) ? keys[0] : keys;
                                  setSelectedUserId(key ? parseInt(key as string) : null);
                                }}
                              >
                                {filteredTimesheetData.map((user) => renderUserCard(user))}
                              </Collapse>
                            ) : (
                              <div className="text-center py-5">
                                <p className="text-muted">
                                  Seçilen tarih aralığında puantaj verisi bulunamadı.
                                </p>
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    )
                  },
                  {
                    key: 'payroll',
                    label: (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontSize: '16px',
                        fontWeight: '600',
                        padding: '4px 8px'
                      }}>
                        <MoneyCollectOutlined style={{ fontSize: '18px', color: '#52c41a' }} />
                        <span>Maaş</span>
                      </div>
                    ),
                    children: (
                      <div style={{
                        height: 'calc(100vh - 200px)',
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        padding: '0 20px 20px 20px'
                      }}>
                        {/* <div style={{ 
                          background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)', 
                          borderRadius: '12px', 
                          padding: '16px',
                          marginBottom: '16px',
                          position: 'sticky',
                          top: '0px',
                          zIndex: 10,
                          backdropFilter: 'blur(8px)',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                        }}>
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between',
                            marginBottom: '12px'
                          }}>
                            <Title level={4} style={{ margin: 0, color: '#52c41a' }}>
                              💰 Maaş Detayları
                            </Title>
                            <span className="text-muted" style={{ fontSize: '12px' }}>
                              {dateRange[0].format("MMMM YYYY")}
                            </span>
                          </div>
                        </div> */}

                        <div style={{ paddingTop: '10px' }}>
                          {payrollLoading ? (
                            <LoadingSpinner centered />
                          ) : (
                            filteredPayrollData && filteredPayrollData.length > 0 ? (
                              <Collapse
                                className="user-cards pr-panel"
                                expandIcon={({ isActive }) => <DownOutlined rotate={isActive ? 180 : 0} />}
                                expandIconPosition="end"
                                ghost
                                accordion
                                activeKey={selectedUserId ? [selectedUserId.toString()] : []}
                                onChange={(keys) => {
                                  const key = Array.isArray(keys) ? keys[0] : keys;
                                  setSelectedUserId(key ? parseInt(key as string) : null);
                                }}
                              >
                                {filteredPayrollData.map(u => renderPayrollUserPanel(u))}
                              </Collapse>
                            ) : (
                              <div className="text-center py-5">
                                <p className="text-muted">
                                  Bu ay için maaş verisi bulunamadı.
                                </p>
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    )
                  }
                ]}
              />
            </Card>
          </div>
        </div>
      </div>
      <FooterSection />
    </div>
  );
};

export default TimesheetReport;
